<template>
  <section :disabled="createdRelease">
    <h2 class="title">Prepare a new Release</h2>
    <p v-if="codeDiffUrl">
      <a :href="codeDiffUrl" target="_blank">Preview changes on GitHub</a>.
    </p>

    <details>
      <summary>Improve changelog readability by using emoji</summary>
      <ul>
        <li>✨ improvement</li>
        <li>🐞 bugfix</li>
        <li>⚙️ technical issue️</li>
        <li>⚡ urgent</li>
        <li>🛁 cleanup</li>
        <li>⭐ minor feature</li>
        <li>👑 major feature</li>
      </ul>
    </details>

    <p>
      Please write clean/meaningful changelogs so that non-technical staff can
      also read and understand it. Example:
    </p>

    <ul>
      <li>
        <code>
          - ⭐ Associate Bootcamp with Course and BootcampEnrolment with
          CourseEnrolment</code
        >
      </li>
      <li><code> - ✨ Hide search bar from Installments Nova panel</code></li>
      <li>
        <code>
          - 🐞 Fix Masterclass record purchase when paid throguh PayPal</code
        >
      </li>
    </ul>

    <p>
      Please mark all technical changes with the ⚙️ emoji, so that
      non-developers know to ignore them. Example: <br />
      <code> - ⚙️ Update composer dependencies</code>
    </p>

    <form @submit.prevent="createRelease">
      <div class="field">
        <label for="releaseName" class="label">Release name</label>
        <input
          id="releaseName"
          v-model="releaseName"
          type="text"
          placeholder="Example: 3.10.2"
          class="input"
          required
          minlength="5"
        />
      </div>
      <div class="field">
        <label for="releaseNotes" class="label">Release notes</label>
        <textarea
          id="releaseNotes"
          v-model="releaseNotes"
          class="textarea"
          rows="10"
          placeholder="Release notes"
        />
        <small
          >Feel free to change autogenerated text to adapt it for
          non-developers</small
        >
      </div>
      <div>
        <button type="submit" class="button is-success">
          <b>Start a chain</b>: Create a new git tag & GitHub release ➡︎ Run CI
          build ➡︎ Deploy (if CI build is green)
        </button>
      </div>
    </form>
  </section>
</template>

<script>
import emojiRegex from 'emoji-regex'

export default {
  name: 'Release',
  props: {
    milestone: {
      type: Object,
      required: true,
    },
    octoRestRepoClient: {
      type: Object,
      required: true,
    },
    octoGraphClient: {
      type: Object,
      required: true,
    },
    targetBranch: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      releaseName: '',
      releaseNotes: '',
      createdRelease: undefined,
      previousRelease: undefined,
    }
  },
  watch: {
    milestone: function(selectedMilestone) {
      this.releaseName = this.generateReleaseName(selectedMilestone)
      this.releaseNotes = this.generateReleaseNotes(selectedMilestone)
    },
  },
  computed: {
    codeDiffUrl: function() {
      if (!this.previousRelease) {
        return undefined
      }
      return `https://github.com/InteractionDesignFoundation/IxDF-web/compare/${this.previousRelease.tagName}...${this.targetBranch}`
    },
  },
  created: function() {
    this.fetchLastReleases().then(releases => {
      this.previousRelease = releases.find(
        release => release.isDraft === false && release.isPrerelease === false
      )
      this.releaseName = this.generateReleaseName(this.milestone)
      this.releaseNotes = this.generateReleaseNotes(this.milestone)
    })
  },
  methods: {
    fetchLastReleases: function() {
      const query = `
                 query {
                  repository(owner: "InteractionDesignFoundation", name: "IxDF-web") {
                    releases(first: 5, orderBy: {field: CREATED_AT, direction: DESC}) {
                      nodes {
                        name
                        tagName
                        isDraft
                        isPrerelease
                        author {
                          name
                        }
                        publishedAt
                      }
                    }
                  }
                }`
      return this.octoGraphClient
        .json({query: query})
        .post()
        .json(json => json.data.repository.releases.nodes)
    },
    generateReleaseName: function(milestone) {
      const semverNumbers = milestone.title.match(/[0-9.]+/)
      return semverNumbers ? semverNumbers[0] : '?'
    },
    generateReleaseNotes: function(milestone) {
      const changelogItems = new Set()
      milestone.issues.nodes
        .filter(issue => {
          const labelNames = issue.labels.nodes.map(label => label.name)
          return !labelNames.includes('hide from release notes')
        })
        .forEach(issue => {
          changelogItems.add(` - ${this.getChangelogTextForIssue(issue)}\n`)
        })
      const head = 'What’s Changed:'
      const body = `${[...changelogItems].join('')}`
      let footer = `Full Changelog: https://github.com/InteractionDesignFoundation/IxDF-web/compare/${this.previousRelease.tagName}...${this.releaseName}`
      if (milestone.issues.nodes.length > 0) {
        footer += `\nClosed issues: ${milestone.url}?closed=1`
      }

      return `${head}\n${body}\n${footer}`
    },
    getChangelogTextForIssue: function(issue) {
      const prefixes = issue.labels.nodes.map(label => {
        const regex = emojiRegex()
        let match
        const emojies = []
        // eslint-disable-next-line no-cond-assign
        while ((match = regex.exec(label.name))) {
          emojies.push(match[0])
        }
        return emojies.join('')
      })

      return `${prefixes.join('')} ${issue.title}`
    },
    createRelease: function() {
      /** @see https://developer.github.com/v3/repos/releases/#create-a-release */
      return this.octoRestRepoClient
        .url(`/releases`)
        .json({
          tag_name: this.releaseName,
          target_commitish: this.targetBranch,
          name: this.releaseName,
          body: this.releaseNotes,
          draft: false,
        })
        .post()
        .json(createdRelease => {
          this.$emit('release-created', createdRelease)
          return (this.createdRelease = createdRelease)
        })
    },
  },
}
</script>

<style scoped>
.textarea {
  width: 100%;
}
</style>
